import * as React from 'react';
import styled from 'styled-components';

import { Button } from '../ui-elements/Main/Button';
import { colors } from '../ui-elements/Main/MainTheme';
import { formatNANP, usePhoneVerification } from './use-phone-verification.hook';

interface PhoneVerifyFormProps {
  verify: ReturnType<typeof usePhoneVerification>;
  onCancel?: () => void;
  onConfirmed?: () => void;
}

// Send-code and enter-code steps shared by the phone gate page and the in-app verify modal.
export const PhoneVerifyForm = ({ verify, onCancel, onConfirmed }: PhoneVerifyFormProps) => {
  const submitPhone = (event: React.FormEvent) => {
    event.preventDefault();
    verify.sendCode();
  };

  const submitCode = async (event: React.FormEvent) => {
    event.preventDefault();
    if (await verify.confirmCode()) onConfirmed?.();
  };

  if (verify.stage === 'code') {
    return (
      <Form onSubmit={submitCode}>
        <Label>
          Enter the 6-digit code sent to <PhoneNumber>{verify.phoneFormatted}</PhoneNumber>
        </Label>
        <CodeInput
          type="tel"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          autoComplete="one-time-code"
          aria-label="Verification code"
          placeholder="••••••"
          value={verify.code}
          onChange={(e) => verify.setCode(e.target.value)}
          required
          autoFocus
        />
        <Hint>Code expires in 10 minutes.</Hint>
        {verify.error && <ErrorMessage>{verify.error}</ErrorMessage>}
        <Actions>
          <Button theme="cancel" size="medium" type="button" onClick={verify.useDifferentNumber}>
            Use a different number
          </Button>
          <Button theme="primary" size="medium" type="submit" disabled={verify.submitting || verify.code.length !== 6}>
            {verify.submitting ? 'Verifying…' : 'Verify'}
          </Button>
        </Actions>
      </Form>
    );
  }

  return (
    <Form onSubmit={submitPhone}>
      <Label htmlFor="trial-verify-phone">Mobile phone number</Label>
      <PhoneInputRow $valid={verify.phoneComplete}>
        <CountryPrefix>+1</CountryPrefix>
        <DigitsInput
          id="trial-verify-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          placeholder="(555) 555-1234"
          value={formatNANP(verify.phoneDigits)}
          onChange={(e) => verify.setPhoneDigits(e.target.value)}
          required
          autoFocus
        />
      </PhoneInputRow>
      <Hint>US or Canadian mobile only. We&apos;ll text you a 6-digit code.</Hint>
      {/* TCPA / CTIA / TCR opt-in disclosure, required wherever we collect a phone */}
      <TcpaDisclosure>
        By providing your phone number, you agree to receive SMS
        messages from Avochato. Msg &amp; data rates may apply. Msg
        frequency varies. Reply STOP to opt out, HELP for help.
        <br />
        <TcpaLinks>
          <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          <span aria-hidden> | </span>
          <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a>
        </TcpaLinks>
      </TcpaDisclosure>
      {verify.error && <ErrorMessage>{verify.error}</ErrorMessage>}
      <Actions>
        {onCancel && (
          <Button theme="cancel" size="medium" type="button" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button theme="primary" size="medium" type="submit" disabled={verify.submitting || !verify.phoneComplete}>
          {verify.submitting ? 'Sending…' : 'Send code'}
        </Button>
      </Actions>
    </Form>
  );
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.textBlack};
  margin-bottom: 6px;
`;

const PhoneNumber = styled.span`
  color: ${colors.darkGreen};
  font-weight: 700;
`;

// Declare props WITHOUT the `$` prefix: styled-components v6's MakeTransient adds it (see CLAUDE.md).
const PhoneInputRow = styled.div<{ valid: boolean }>`
  display: flex;
  align-items: stretch;
  border: 1px solid ${({ $valid }) => ($valid ? colors.darkGreen : colors.greyer)};
  border-radius: 6px;
  overflow: hidden;
  background: ${colors.white};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: ${colors.darkGreen};
    box-shadow: 0 0 0 2px rgba(30, 112, 90, 0.18);
  }
`;

const CountryPrefix = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 14px;
  background: ${colors.lightestGray};
  border-right: 1px solid ${colors.greyer};
  font-size: 15px;
  font-weight: 600;
  color: ${colors.textBlack};
  font-family: Montserrat, sans-serif;
  user-select: none;
`;

const DigitsInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  padding: 12px 14px;
  font-size: 16px;
  font-family: Montserrat, sans-serif;
  color: ${colors.textBlack};
  background: ${colors.white};

  &::placeholder {
    color: ${colors.steel};
  }
`;

const CodeInput = styled.input`
  padding: 12px 16px;
  border: 1px solid ${colors.greyer};
  border-radius: 4px;
  font-size: 22px;
  letter-spacing: 8px;
  text-align: center;
  font-family: Montserrat, sans-serif;
  color: ${colors.textBlack};

  &:focus {
    border-color: ${colors.darkGreen};
    box-shadow: 0 0 0 1px ${colors.darkGreen};
    outline: none;
  }

  &::placeholder {
    color: ${colors.greyer};
    letter-spacing: 8px;
  }
`;

const Hint = styled.span`
  font-size: 12px;
  color: ${colors.steel};
  margin-top: 4px;
`;

const TcpaDisclosure = styled.div`
  font-size: 11px;
  line-height: 1.45;
  color: ${colors.steel};
  margin-top: 10px;
`;

const TcpaLinks = styled.span`
  display: inline-block;
  margin-top: 4px;

  a {
    color: ${colors.steel};
    text-decoration: underline;
    &:hover { color: ${colors.darkGreen}; }
  }

  span {
    margin: 0 4px;
    color: ${colors.greyer};
  }
`;

const ErrorMessage = styled.div`
  background: ${colors.errorRed};
  color: ${colors.red};
  padding: 10px 14px;
  border-radius: 4px;
  font-size: 13px;
  margin-top: 8px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 28px;
`;
